import { Menu, MenuItem, Stack, Typography } from '@mui/material'
import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Icons } from 'utoniq-ui'

import {
  Maybe,
  ResaleConnectionFragment,
  ResaleRaffleConnectionFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import {
  ResaleItemBox,
  ResaleItemBoxSkeleton,
  ResaleItemStatus,
  ResaleRaffleStatus,
} from '@/components/organisms'
import { ScrollFetcher, Tabs } from '@/components/organisms/functionals'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  tab: number
  resales: Maybe<ResaleConnectionFragment>
  resaleRaffles: Maybe<ResaleRaffleConnectionFragment>
  loading: boolean
  hasNextPage: boolean
  hasNextPageRaffles: boolean
  filter: string
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: (
    type: 'listed' | 'raffle' | 'purchased',
    status?: string[]
  ) => Promise<void>
  onFilter: (
    type: 'listed' | 'raffle' | 'purchased',
    status?: string[]
  ) => Promise<void>
}

export const ResaleManagementTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
  onFilter,
}) => {
  const { t } = useTranslation('resaleManagement')
  const { routerNames, router, generatePath, routerPages } = useRouter()

  const tabs = useMemo<
    { label: string; value: 'listed' | 'raffle' | 'purchased' }[]
  >(
    () => [
      {
        label: t('listed'),
        value: 'listed',
      },
      {
        label: t('raffle'),
        value: 'raffle',
      },
      {
        label: t('purchased'),
        value: 'purchased',
      },
    ],
    [t]
  )

  const handleChangeTab = useCallback(
    async (newValue: number) => {
      const tabs: Record<number, string> = {
        1: 'raffle',
        2: 'purchased',
      }
      router.replace(
        generatePath(routerPages.ResaleManagement, { tab: tabs[newValue] })
      )
    },
    [generatePath, router, routerPages.ResaleManagement]
  )

  return (
    <>
      <Head title={routerNames.ResaleManagement} />
      <BaseLayout
        header={{
          back: true,
          defaultBackPage: routerPages.Management,
          title: t('title'),
        }}
      >
        <Tabs
          value={state.tab}
          onChange={handleChangeTab}
          disableAnimateTransitions
          labels={tabs.map(({ label }) => label)}
        >
          <ResaleCell
            type="listed"
            resales={state.resales}
            loading={state.loading}
            hasNextPage={state.hasNextPage}
            onFetchMore={onFetchMore}
            onFilter={onFilter}
            filter={state.filter}
          />
          <ResaleCell
            type="raffle"
            resales={state.resaleRaffles}
            loading={state.loading}
            hasNextPage={state.hasNextPageRaffles}
            onFetchMore={onFetchMore}
            onFilter={onFilter}
            filter={state.filter}
          />
          <ResaleCell
            type="purchased"
            resales={state.resaleRaffles}
            loading={state.loading}
            hasNextPage={state.hasNextPageRaffles}
            onFetchMore={onFetchMore}
            onFilter={onFilter}
            filter={state.filter}
          />
        </Tabs>
      </BaseLayout>
    </>
  )
}

type ResaleCellProps = {
  type: 'listed' | 'raffle' | 'purchased'
  resales: State['resales'] | State['resaleRaffles']
  loading: State['loading']
  filter: string

  hasNextPage: State['hasNextPage']
  onFetchMore: Action['onFetchMore']
  onFilter: Action['onFilter']
}
const ResaleCell: Cell<ResaleCellProps> = ({
  type,
  resales,
  loading,
  hasNextPage,
  filter,
  onFetchMore,
  onFilter,
}) => {
  const { t } = useTranslation('resaleManagement')
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const options = useMemo(() => {
    if (type === 'listed') {
      return [
        { label: t('all'), value: undefined, param: 'all' },
        {
          label: t('exhibiting'),
          value: [ResaleItemStatus.Open, ResaleItemStatus.Matched],
          param: 'exhibiting',
        },
        {
          label: t('failedToSell'),
          value: [ResaleItemStatus.NoMatched],
          param: 'failedToSell',
        },
        {
          label: t('sold'),
          value: [ResaleItemStatus.Sold, ResaleItemStatus.Paid],
          param: 'sold',
        },
        {
          label: t('cancel'),
          value: [ResaleItemStatus.Canceled],
          param: 'cancel',
        },
      ]
    }
    return [
      {
        label: t('all'),
        value: [
          ResaleRaffleStatus.Win,
          ResaleRaffleStatus.Applied,
          ResaleRaffleStatus.Lost,
          ResaleRaffleStatus.Invalid,
          ResaleRaffleStatus.Canceled,
        ],
        param: 'whole',
      },
      {
        label: t('waitingPayment'),
        value: [ResaleRaffleStatus.Win],
        param: 'waitingPayment',
      },
      {
        label: t('waitingLottery'),
        value: [ResaleRaffleStatus.Applied],
        param: 'waitingLottery',
      },
      {
        label: t('lost'),
        value: [ResaleRaffleStatus.Lost],
        param: 'lost',
      },
      {
        label: t('invalid'),
        value: [ResaleRaffleStatus.Invalid],
        param: 'invalid',
      },
      {
        label: t('cancel'),
        value: [ResaleRaffleStatus.Canceled],
        param: 'revoke',
      },
    ]
  }, [t, type])

  const selectedOptionIndex = options.findIndex(
    option => option.param === filter
  )
    ? options.findIndex(option => option.param === filter)
    : 0

  const [selectedIndex, setSelectedIndex] = useState<number>(
    selectedOptionIndex !== -1 ? selectedOptionIndex : 0
  )

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  useEffect(() => {
    if (type !== 'purchased') {
      const UrlParams = `?filter=${options[selectedIndex].param}`
      // React Routerのhistoryを使うとソート順を変えた際に、スクロール位置が一番上になるため、JavaScriptで直接historyを利用している
      window.history.replaceState(null, '', UrlParams)
    }
  }, [selectedIndex])

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleMenuItemClick = useCallback(
    async (event: MouseEvent<HTMLElement>, index: number) => {
      setSelectedIndex(index)
      setAnchorEl(null)
      await onFilter(type, options[index]?.value)
    },
    [onFilter, type, options]
  )

  return (
    <CellFrame sx={{ px: 5 }}>
      {type !== 'purchased' && (
        <Stack alignItems="flex-end" sx={{ pb: 2 }}>
          <Button
            variant="text"
            sx={{ width: 'fit-content', mr: -2 }}
            onClick={handleClick}
          >
            <Typography variant="caption" sx={{ pr: 1 }}>
              {options[selectedIndex]?.label}
            </Typography>
            <Icons.DownArrow fontSize="xSmall" sx={{ color: 'grey.600' }} />
          </Button>
        </Stack>
      )}
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleClose}>
        {options.map((option, index) => (
          <MenuItem
            sx={{ bgcolor: 'grey.100', minWidth: 200 }}
            key={index}
            selected={index === selectedIndex}
            onClick={event => handleMenuItemClick(event, index)}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
      <ScrollFetcher
        nodes={resales?.edges.map(({ node }) => (
          <ResaleItemBox resaleItem={node} key={node.id} />
        ))}
        loading={loading}
        hasNextPage={hasNextPage}
        onFetchMore={() => onFetchMore(type, options[selectedIndex]?.value)}
        emptyMessage={t('ticketNotExist')}
        loadingComponent={
          <Stack spacing={4}>
            {Array.from(Array(5).keys()).map(index => (
              <ResaleItemBoxSkeleton key={index} />
            ))}
          </Stack>
        }
      />
    </CellFrame>
  )
}
